import type { ArticleSummary, ExplorationLevel, ExplorationState } from '@rabbit/shared';

export type ExplorationAction =
  | { type: 'SET_ROOT'; items: ArticleSummary[] }
  | { type: 'RESET_ROOT'; items: ArticleSummary[] }
  | { type: 'APPEND_ITEMS'; level: number; items: ArticleSummary[]; exhausted?: boolean }
  | { type: 'NEXT' }
  | { type: 'PREVIOUS' }
  | { type: 'EXPLORE'; anchor: ArticleSummary; items: ArticleSummary[] }
  | { type: 'BACK' }
  | { type: 'HOME' }
  | { type: 'HYDRATE_SHARED'; articles: ArticleSummary[]; pathLength: number };

export const initialExplorationState: ExplorationState = { levels: [{ anchor: null, items: [], index: 0 }], journey: [] };

function uniqueItems(existing: ArticleSummary[], incoming: ArticleSummary[]): ArticleSummary[] {
  const ids = new Set(existing.map((item) => `${item.lang}:${item.id}:${item.title.toLocaleLowerCase()}`));
  return [...existing, ...incoming.filter((item) => {
    const key = `${item.lang}:${item.id}:${item.title.toLocaleLowerCase()}`;
    if (ids.has(key)) return false;
    ids.add(key);
    return true;
  })];
}

export function explorationReducer(state: ExplorationState, action: ExplorationAction): ExplorationState {
  switch (action.type) {
    case 'SET_ROOT': {
      const root = { anchor: null, items: uniqueItems(state.levels[0]?.items ?? [], action.items), index: state.levels[0]?.index ?? 0 };
      const levels = state.levels.length ? [...state.levels] : [root];
      levels[0] = root;
      return { ...state, levels };
    }
    case 'RESET_ROOT':
      return { levels: [{ anchor: null, items: uniqueItems([], action.items), index: 0 }], journey: [] };
    case 'APPEND_ITEMS': {
      const levels = state.levels.map((level, index) => index === action.level ? { ...level, items: uniqueItems(level.items, action.items), ...(action.exhausted === undefined ? {} : { exhausted: action.exhausted }) } : level);
      return { ...state, levels };
    }
    case 'NEXT': {
      const levels = [...state.levels];
      const active = levels.at(-1);
      if (!active || active.index >= active.items.length - 1) return state;
      levels[levels.length - 1] = { ...active, index: active.index + 1 };
      return { ...state, levels };
    }
    case 'PREVIOUS': {
      const levels = [...state.levels];
      const active = levels.at(-1);
      if (!active || active.index <= 0) return state;
      levels[levels.length - 1] = { ...active, index: active.index - 1 };
      return { ...state, levels };
    }
    case 'EXPLORE':
      return action.items.length ? {
        levels: [...state.levels, { anchor: action.anchor, items: uniqueItems([], action.items.filter((item) => item.title !== action.anchor.title)), index: 0 }],
        journey: [...state.levels.slice(1).flatMap((level) => level.anchor ? [level.anchor] : []), action.anchor],
      } : state;
    case 'BACK':
      return state.levels.length > 1 ? { ...state, levels: state.levels.slice(0, -1) } : state;
    case 'HOME':
      return { ...state, levels: state.levels.slice(0, 1) };
    case 'HYDRATE_SHARED': {
      if (!action.articles.length) return state;
      const path = action.articles.slice(0, action.pathLength);
      const current = action.articles[action.pathLength] ?? path.at(-1);
      if (!current || !path[0]) return state;
      const levels: ExplorationLevel[] = [{ anchor: null, items: [path[0]], index: 0 }];
      for (let index = 0; index < path.length; index += 1) {
        const anchor = path[index];
        const candidate = path[index + 1] ?? current;
        if (anchor && candidate) levels.push({ anchor, items: [candidate], index: 0 });
      }
      return { levels, journey: path };
    }
  }
}

export function activeLevel(state: ExplorationState): ExplorationLevel {
  return state.levels.at(-1) ?? initialExplorationState.levels[0]!;
}

export function currentArticle(state: ExplorationState): ArticleSummary | undefined {
  const level = activeLevel(state);
  return level.items[level.index];
}

export function journeyPath(state: ExplorationState): ArticleSummary[] {
  return state.journey;
}
